import React, { useEffect, useMemo, useState } from 'react';
import { reportHandledError } from '../utils/sentry';
import { supabase } from '../supabase';
import { useAuth } from '../context/AuthContext';
import { useSite } from '../context/SiteContext';
import { useNavigate } from 'react-router-dom';
import {
  Users, DollarSign, TrendingUp, Activity, Wallet, AlertCircle, RefreshCcw,
  ShoppingCart, Package, BarChart3, BadgeAlert, ArrowRight, Landmark,
  Calendar, Target, ArrowLeft, Calculator, Scale, Info, ChevronRight, CheckCircle2,
  Filter, Clock, ArrowDownCircle, ArrowUpCircle, TrendingDown, Download, MapPin
} from 'lucide-react';
import { fetchAllPaginated, getProductStockForSite } from '../utils/productCatalog';
import { fetchStoreSiteContext, isStoreOrderFromWeb, resolveStoreOrderSiteId } from '../utils/storeLogistics';
import { attachStoreOrdersToSales, isSaleAwaitingWebFulfillment } from '../utils/saleStoreOrders';
import {
  readFirstSaleOnboarding,
  FIRST_SALE_ONBOARDING_EVENT,
  FIRST_SALE_ONBOARDING_STEPS,
  buildFirstSaleOnboardingDefinition,
  getFirstSaleOnboardingRoute,
  resumeFirstSaleOnboarding,
} from '../utils/firstSaleOnboarding';
import {
  buildSetupOnboardingChecklist,
  getSetupOnboardingProgress,
  getSetupOnboardingBrandName,
  shouldShowSetupOnboarding,
  fetchSetupOnboardingSignals,
  fetchSetupOnboardingState,
  completeSetupOnboardingStep,
  dismissSetupOnboarding,
} from '../utils/setupOnboarding';
import { logOnboardingEvent, loadOnboardingProgress, saveOnboardingProgress } from '../utils/onboardingTelemetry';

// ============================================================
// FILTROS DE VENTAS (espejo de VentasHistorial)
// ============================================================
// Estos filtros deben ser idénticos a los de VentasHistorial.jsx para que el
// número que muestra el Dashboard coincida con el listado del historial.
// Antes el Dashboard leía de un RPC que incluía presupuestos, ventas anuladas
// y/o pedidos web pendientes; por eso los saldos diferían.
const DASH_LEGACY_CRM_SOURCES = ['crm_hito', 'crm_proyecto', 'crm_ticket', 'crm_schedule'];
const DASH_LEGACY_CRM_SOURCE_FILTER = DASH_LEGACY_CRM_SOURCES.join(',');
const buildDashHistorySiteFilter = (siteId) =>
  `site_id.eq.${siteId},and(site_id.is.null,billing_source.in.(${DASH_LEGACY_CRM_SOURCE_FILTER}))`;

const getTodayYMD = () => new Date().toLocaleDateString('en-CA');
const getFirstOfMonthYMD = () => {
  const d = new Date();
  return new Date(d.getFullYear(), d.getMonth(), 1).toLocaleDateString('en-CA');
};

/**
 * Dashboard Operativo - Diseño Nova 
 * Estilo original mantenido (fuentes, tamaños y KPIs en tablet intactos).
 * FIX: Gaps reducidos y cabecera de gráfico dinámica al hacer hover en las columnas.
 */

const formatMoney = (amount) => {
  return '$ ' + Number(amount || 0).toLocaleString('es-AR', { minimumFractionDigits: 0, maximumFractionDigits: 0 }) + '.-';
};

const DashboardKPI = ({
  title,
  value,
  subtext,
  icon,
  tone = 'blue',
  onClick,
  dataOnboardingId,
}) => {
  const colorMap = {
    emerald: 'text-emerald-600',
    rose: 'text-rose-700',
    amber: 'text-amber-700',
    blue: 'text-blue-600',
    slate: 'text-slate-800'
  };

  const Container = onClick ? 'button' : 'div';

  return (
    <Container
      onClick={onClick}
      data-onboarding-id={dataOnboardingId}
      className={`imp-kpi text-left transition-all ${onClick ? 'hover:bg-slate-50/40 cursor-pointer' : ''}`}
    >
      <div className="flex items-center justify-between">
        <p className="text-[9px] font-black uppercase tracking-widest text-slate-400">{title}</p>
        <div className="text-slate-300">
          {React.cloneElement(icon, { size: 16 })}
        </div>
      </div>
      <p className={`mt-1.5 text-2xl font-black tracking-tight ${colorMap[tone] || 'text-slate-800'}`}>
        {value}
      </p>
      <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400">{subtext}</p>
    </Container>
  );
};

const AttentionAlertCard = ({
  title,
  summary,
  description,
  count,
  icon,
  tone,
  expanded,
  loading,
  items,
  onToggle,
  onViewAll,
}) => {
  const toneMap = {
    emerald: {
      card: 'bg-emerald-50 border-emerald-100 hover:bg-emerald-100',
      title: 'text-emerald-800',
      text: 'text-emerald-600',
      icon: 'text-emerald-500',
      badge: 'bg-emerald-100 text-emerald-700 border-emerald-200',
      item: 'hover:border-emerald-200 hover:bg-emerald-50/70',
      bullet: 'bg-emerald-400',
      button: 'text-emerald-700 hover:text-emerald-800',
    },
    amber: {
      card: 'bg-amber-50 border-amber-100 hover:bg-amber-100',
      title: 'text-amber-800',
      text: 'text-amber-600',
      icon: 'text-amber-500',
      badge: 'bg-amber-100 text-amber-700 border-amber-200',
      item: 'hover:border-amber-200 hover:bg-amber-50/70',
      bullet: 'bg-amber-400',
      button: 'text-amber-700 hover:text-amber-800',
    },
    rose: {
      card: 'bg-rose-50 border-rose-100 hover:bg-rose-100',
      title: 'text-rose-800',
      text: 'text-rose-600',
      icon: 'text-rose-500',
      badge: 'bg-rose-100 text-rose-700 border-rose-200',
      item: 'hover:border-rose-200 hover:bg-rose-50/70',
      bullet: 'bg-rose-400',
      button: 'text-rose-700 hover:text-rose-800',
    },
    blue: {
      card: 'bg-blue-50 border-blue-100 hover:bg-blue-100',
      title: 'text-blue-800',
      text: 'text-blue-600',
      icon: 'text-blue-500',
      badge: 'bg-blue-100 text-blue-700 border-blue-200',
      item: 'hover:border-blue-200 hover:bg-blue-50/70',
      bullet: 'bg-blue-400',
      button: 'text-blue-700 hover:text-blue-800',
    },
  };

  const styles = toneMap[tone] || toneMap.blue;

  return (
    <div className={`rounded-xl border transition-colors ${styles.card}`}>
      <button
        type="button"
        onClick={onToggle}
        className="w-full flex justify-between items-center p-3 text-left group"
      >
        <div className="flex items-center gap-3 min-w-0">
          <div className={`${styles.icon} shrink-0`}>
            {React.cloneElement(icon, { size: 16 })}
          </div>
          <div className="min-w-0">
            <p className={`text-[9px] font-black uppercase tracking-widest ${styles.title}`}>{title}</p>
            <p className={`text-[9px] mt-0.5 ${styles.text}`}>
              <span className="font-black">{count}</span> {summary}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <span className={`px-2 py-1 rounded-lg border text-[8px] font-black uppercase tracking-widest ${styles.badge}`}>
            {count}
          </span>
          <ChevronRight
            size={14}
            className={`transition-transform ${styles.icon} ${expanded ? 'rotate-90' : 'group-hover:translate-x-0.5'}`}
          />
        </div>
      </button>

      {expanded && (
        <div className="px-3 pb-3 pt-0 border-t border-white/50">
          <p className="pt-3 text-[9px] font-bold text-slate-500 leading-relaxed">
            {description}
          </p>

          <div className="mt-3 space-y-2">
            {loading ? (
              <div className="flex items-center justify-center py-5 text-slate-300">
                <RefreshCcw size={16} className="animate-spin" />
              </div>
            ) : items.length > 0 ? (
              items.map((item) => (
                <button
                  key={item.id}
                  type="button"
                  onClick={item.onClick}
                  className={`w-full flex justify-between items-center gap-3 p-2.5 rounded-xl border border-transparent bg-white/70 transition-all ${styles.item}`}
                  title={item.meta}
                >
                  <div className="min-w-0 flex items-start gap-2">
                    <span className={`mt-1 h-1.5 w-1.5 rounded-full shrink-0 ${styles.bullet}`} />
                    <div className="min-w-0">
                      <p className="text-[9px] font-black text-slate-700 uppercase tracking-wider truncate">{item.label}</p>
                      <p className="text-[9px] font-bold text-slate-400 truncate">{item.meta}</p>
                    </div>
                  </div>
                  <ArrowRight size={13} className={`${styles.icon} shrink-0`} />
                </button>
              ))
            ) : (
              <div className="rounded-xl border border-dashed border-slate-200 bg-white/50 px-3 py-4 text-center">
                <p className="text-[9px] font-black uppercase tracking-widest text-slate-300">Sin detalle puntual</p>
              </div>
            )}
          </div>

          <button
            type="button"
            onClick={onViewAll}
            className={`mt-3 text-[9px] font-black uppercase tracking-widest transition-colors ${styles.button}`}
          >
            Ver alerta completa
          </button>
        </div>
      )}
    </div>
  );
};

const Dashboard = () => {
  const { profile } = useAuth();
  const navigate = useNavigate();
  const orgId = profile?.organization_id;

  // ✅ FIX multi-sucursal: leemos sitios y selección desde el SiteContext global
  // (sin duplicar fetch ni quedar desincronizados al cambiar de sucursal).
  const { sites: sitiosCtx, selectedSiteId: activeSiteId } = useSite();

  const [loading, setLoading] = useState(true);
  const [loadingAlerts, setLoadingAlerts] = useState(false);
  const [sitios, setSitios] = useState([]);
  const [expandedAlertKey, setExpandedAlertKey] = useState(null);
  const [alertDetails, setAlertDetails] = useState({
    orders: [],
    lowStock: [],
    lowMargin: [],
    dueToday: [],
  });

  // ESTADO: Hover dinámico en gráfico
  const [hoveredDay, setHoveredDay] = useState(null);

  // ESTADO: Filtro independiente para "Más Vendidos"
  const [showTopProdFilter, setShowTopProdFilter] = useState(false);
  const [topProdFilter, setTopProdFilter] = useState({
    from: new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString().split('T')[0],
    to: new Date().toISOString().split('T')[0]
  });
  const [topProductsList, setTopProductsList] = useState([]);
  const [loadingTopProds, setLoadingTopProds] = useState(false);

  // ESTADO: PWA Install Prompt
  const [deferredPrompt, setDeferredPrompt] = useState(null);

  // ============================================================
  // FILTRO DE FECHA DE LOS KPIs DE VENTAS
  // ============================================================
  // El Dashboard antes solo mostraba "Ventas Hoy" y "Ventas Mes" con totales
  // fijos del RPC. Ahora exponemos un rango (desde/hasta) que el usuario
  // puede mover, y recalculamos los totales con la MISMA query que usa
  // VentasHistorial.jsx (active=true, sin presupuestos, sin pedidos web
  // pendientes). Así los saldos coinciden entre Dashboard e Historial.
  const [kpiDateRange, setKpiDateRange] = useState({
    from: getTodayYMD(),
    to: getTodayYMD(),
  });
  const [showKpiDatePicker, setShowKpiDatePicker] = useState(false);
  const [kpiVentas, setKpiVentas] = useState({ total: 0, count: 0, ticketPromedio: 0 });
  const [loadingKpis, setLoadingKpis] = useState(false);
  const kpiDatePickerRef = React.useRef(null);

  // ============================================================
  // DEMO GUIADA
  // ============================================================
  // El estado del recorrido de primera venta solo decide QUE se muestra
  // (panel de setup, avisos), nunca inventa cifras: los KPIs son siempre
  // datos reales, aunque una org nueva arranque en $0.
  const [firstSaleDemo, setFirstSaleDemo] = useState(() => readFirstSaleOnboarding());

  useEffect(() => {
    const sync = () => setFirstSaleDemo(readFirstSaleOnboarding());
    sync();
    window.addEventListener(FIRST_SALE_ONBOARDING_EVENT, sync);
    return () => window.removeEventListener(FIRST_SALE_ONBOARDING_EVENT, sync);
  }, []);

  // 1.3 — Recorrido pausado: datos para el CTA "Retomar" dentro del checklist.
  const resumeTour = useMemo(() => {
    if (firstSaleDemo?.status !== 'paused') return null;
    const idx = FIRST_SALE_ONBOARDING_STEPS.indexOf(firstSaleDemo.stepId);
    return {
      stepLabel: buildFirstSaleOnboardingDefinition(firstSaleDemo.stepId)?.title || null,
      stepIndex: idx >= 0 ? idx + 1 : null,
      stepTotal: FIRST_SALE_ONBOARDING_STEPS.length,
      onResume: () => {
        const resumed = resumeFirstSaleOnboarding();
        const route = resumed?.stepId ? getFirstSaleOnboardingRoute(resumed.stepId) : null;
        if (route) navigate(route);
      },
    };
  }, [firstSaleDemo, navigate]);

  const isDemoActive = firstSaleDemo?.status === 'active';

  // ── Checklist "Empeza a operar" (etapa 5 del onboarding) ──
  const [setupChecklist, setSetupChecklist] = useState([]);
  const [setupState, setSetupState] = useState(null);
  const [setupBusyStep, setSetupBusyStep] = useState(null);
  // 1.5 — solo instrumentamos el checklist para la cohorte v2 (no ensuciar el
  // funnel con orgs existentes). Refs para no re-emitir "completado".
  const [onboardingV2, setOnboardingV2] = useState(false);
  const checklistCompletedLoggedRef = React.useRef(false);
  const prevAllRequiredDoneRef = React.useRef(null);

  const loadSetupOnboarding = React.useCallback(async () => {
    if (!orgId) {
      setSetupChecklist([]);
      return;
    }
    try {
      const [signals, state] = await Promise.all([
        fetchSetupOnboardingSignals(orgId),
        fetchSetupOnboardingState(orgId),
      ]);
      setSetupState(state);
      // Flag de cohorte v2 + rubro (best-effort; si falta la columna, queda en false).
      // El checklist por vertical (F5) solo se aplica a la cohorte v2: las orgs
      // existentes siguen viendo el checklist base de comercio.
      let bizType = null;
      try {
        const { data: orgRow } = await supabase
          .from('organizations')
          .select('onboarding_v2_enabled, business_type')
          .eq('id', orgId)
          .maybeSingle();
        const v2 = orgRow?.onboarding_v2_enabled === true;
        setOnboardingV2(v2);
        bizType = v2 ? (orgRow?.business_type || null) : null;
      } catch { /* no-op */ }
      setSetupChecklist(buildSetupOnboardingChecklist(signals, state, bizType));
    } catch (error) {
      console.warn('No se pudo cargar el checklist de configuracion.', error);
      setSetupChecklist([]);
    }
  }, [orgId]);

  useEffect(() => {
    loadSetupOnboarding();
  }, [loadSetupOnboarding]);

  const setupProgress = useMemo(() => getSetupOnboardingProgress(setupChecklist), [setupChecklist]);
  const showSetupChecklist = useMemo(
    () => !isDemoActive && shouldShowSetupOnboarding(setupChecklist, setupState),
    [isDemoActive, setupChecklist, setupState],
  );

  const handleSetupComplete = async (stepId) => {
    if (!orgId) return;
    setSetupBusyStep(stepId);
    try {
      const nextState = await completeSetupOnboardingStep({ organizationId: orgId, stepId, currentState: setupState });
      setSetupState(nextState);
      await loadSetupOnboarding();
      if (onboardingV2) {
        logOnboardingEvent({ event: 'checklist_step_done', stepId, organizationId: orgId, userId: profile?.id });
      }
    } catch (error) {
      console.warn('No se pudo marcar el paso de configuracion.', error);
    } finally {
      setSetupBusyStep(null);
    }
  };

  // 1.5 — checklist_completed: solo al pasar de "incompleto" a "completo" en la
  // sesion (no re-emitir para orgs que ya llegan completas), y solo cohorte v2.
  useEffect(() => {
    const done = setupProgress.allRequiredDone === true;
    const prev = prevAllRequiredDoneRef.current;
    if (prev === false && done && onboardingV2 && !checklistCompletedLoggedRef.current) {
      checklistCompletedLoggedRef.current = true;
      logOnboardingEvent({ event: 'checklist_completed', organizationId: orgId, userId: profile?.id });
    }
    prevAllRequiredDoneRef.current = done;
  }, [setupProgress.allRequiredDone, onboardingV2, orgId, profile?.id]);

  // 2.3 (F6) — Bienvenida por rol para usuarios invitados no-admin (cohorte v2).
  // Se muestra una sola vez por usuario (persistida en onboarding_progress) y
  // los lleva a su area principal de trabajo. Solo aplica a orgs v2, asi que los
  // usuarios de orgs existentes no la ven.
  const isAdminRole = ['admin', 'administrador'].includes(String(profile?.role || '').toLowerCase());
  const [showRoleWelcome, setShowRoleWelcome] = useState(false);
  useEffect(() => {
    const userId = profile?.id;
    if (!userId || !onboardingV2 || isAdminRole) { setShowRoleWelcome(false); return undefined; }
    let alive = true;
    (async () => {
      let seen = false;
      try { seen = window.localStorage.getItem('impulza:role-welcome-seen') === '1'; } catch { /* no-op */ }
      if (!seen) {
        const st = await loadOnboardingProgress({ userId, key: 'role_welcome' });
        seen = st?.seen === true;
      }
      if (alive) setShowRoleWelcome(!seen);
    })();
    return () => { alive = false; };
  }, [profile?.id, onboardingV2, isAdminRole]);

  const dismissRoleWelcome = (goToPos) => {
    setShowRoleWelcome(false);
    try { window.localStorage.setItem('impulza:role-welcome-seen', '1'); } catch { /* no-op */ }
    saveOnboardingProgress({ userId: profile?.id, organizationId: orgId, key: 'role_welcome', state: { seen: true } });
    logOnboardingEvent({ event: 'role_welcome_done', organizationId: orgId, userId: profile?.id });
    if (goToPos) navigate('/sistema/ventas-pos');
  };

  const handleSetupDismiss = async () => {
    if (!orgId) return;
    try {
      const nextState = await dismissSetupOnboarding({ organizationId: orgId, currentState: setupState });
      setSetupState(nextState);
      setSetupChecklist((prev) => [...prev]);
    } catch (error) {
      console.warn('No se pudo ocultar el panel de configuracion.', error);
    }
  };

  // El KPI muestra SIEMPRE las ventas reales. Antes, durante el recorrido
  // guiado, inventaba 6 ventas de $2.450 (=$14.700) para que el panel no se
  // viera vacío: un negocio recién creado arrancaba viendo facturación que
  // nunca existió.
  const kpiVentasDisplay = kpiVentas;

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (kpiDatePickerRef.current && !kpiDatePickerRef.current.contains(e.target)) {
        setShowKpiDatePicker(false);
      }
    };
    if (showKpiDatePicker) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [showKpiDatePicker]);

  const isKpiRangeToday = useMemo(() => {
    const t = getTodayYMD();
    return kpiDateRange.from === t && kpiDateRange.to === t;
  }, [kpiDateRange]);

  const kpiRangeLabel = useMemo(() => {
    const formatDmy = (ymd) => {
      if (!ymd) return '';
      const [y, m, d] = ymd.split('-');
      return `${d}/${m}/${y.slice(2)}`;
    };
    if (isKpiRangeToday) return 'Hoy';
    if (kpiDateRange.from === kpiDateRange.to) return formatDmy(kpiDateRange.from);
    return `${formatDmy(kpiDateRange.from)} – ${formatDmy(kpiDateRange.to)}`;
  }, [isKpiRangeToday, kpiDateRange]);

  const applyKpiPreset = (preset) => {
    const today = getTodayYMD();
    if (preset === 'hoy') {
      setKpiDateRange({ from: today, to: today });
    } else if (preset === 'ayer') {
      const d = new Date();
      d.setDate(d.getDate() - 1);
      const y = d.toLocaleDateString('en-CA');
      setKpiDateRange({ from: y, to: y });
    } else if (preset === 'semana') {
      const d = new Date();
      d.setDate(d.getDate() - 6);
      setKpiDateRange({ from: d.toLocaleDateString('en-CA'), to: today });
    } else if (preset === 'mes') {
      setKpiDateRange({ from: getFirstOfMonthYMD(), to: today });
    }
    setShowKpiDatePicker(false);
  };

  useEffect(() => {
    const handleBeforeInstallPrompt = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
    };
    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    return () => window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
  }, []);

  const handleInstallClick = async () => {
    if (deferredPrompt) {
      deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      if (outcome === 'accepted') {
        setDeferredPrompt(null);
      }
    }
  };

  const [metrics, setMetrics] = useState({
    ventasHoy: 0,
    ventasMes: 0,
    gastosMes: 0,
    ticketPromHoy: 0,
    stockBajo: 0,
    bajaRentabilidad: 0,
    pedidosWeb: 0,
    proyIngresosHoy: 0,
    proyEgresosHoy: 0,
    bancosNegativos: 0,
    saldoCaja: 0,
    puntoEquilibrio: 0,
    costoVentasMes: 0,
    margenContribucion: 0,
    porcentajeMargen: 0,
    ultimasVentas: [],
    monthlyData: [],
    lowStockItems: []
  });

  const siteLabel = useMemo(() => {
    if (!activeSiteId) return 'Sin sucursal';
    return sitios.find((s) => s.id === activeSiteId)?.nombre || 'Sucursal';
  }, [activeSiteId, sitios]);

  // ✅ FIX multi-sucursal: ya no hay listener manual de `storage`.
  // El SiteContext propaga los cambios in-tab y entre pestañas.
  // Mantenemos el `sitios` local sincronizado con el del contexto para
  // no romper consumidores actuales del dashboard.
  useEffect(() => {
    setSitios(Array.isArray(sitiosCtx) ? sitiosCtx : []);
  }, [sitiosCtx]);

  // ✅ Eliminado: el fetch de sitios ahora vive en SiteContext.

  useEffect(() => {
    if (!orgId) return;
    fetchDashboardData();
    fetchAlertDetails();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orgId, activeSiteId]);

  useEffect(() => {
    if (!orgId) return;
    fetchTopProducts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orgId, activeSiteId, topProdFilter]);

  // ============================================================
  // KPI DE VENTAS: misma query que VentasHistorial
  // ============================================================
  // Antes este KPI venía del RPC `get_dashboard_metrics` y no aplicaba los
  // mismos filtros que el Historial (active=true, sin presupuestos, sin
  // pedidos web pendientes). Por eso los totales diferían. Acá replicamos
  // exactamente la lógica del Historial para que ambos números coincidan.
  const fetchSalesKpis = async () => {
    if (!orgId) return;
    setLoadingKpis(true);
    try {
      const start = `${kpiDateRange.from}T00:00:00.000Z`;
      const end = `${kpiDateRange.to}T23:59:59.999Z`;

      let q = supabase
        .from('sales')
        .select('id, total, billing_source, status, site_id, created_at')
        .eq('organization_id', orgId)
        .eq('active', true)
        .neq('invoice_status', 'presupuesto')
        .gte('created_at', start)
        .lte('created_at', end);

      if (activeSiteId) q = q.or(buildDashHistorySiteFilter(activeSiteId));

      const { data, error } = await q;
      if (error) throw error;

      // Adjuntamos store_orders para poder filtrar pedidos web pendientes
      // de despacho (mismo criterio que aplica VentasHistorial).
      const attached = await attachStoreOrdersToSales({
        sales: data || [],
        organizationId: orgId,
        supabaseClient: supabase,
      });
      const visible = (attached.data || []).filter((s) => !isSaleAwaitingWebFulfillment(s));

      const total = visible.reduce((acc, s) => acc + Number(s.total || 0), 0);
      const count = visible.length;
      setKpiVentas({
        total,
        count,
        ticketPromedio: count > 0 ? total / count : 0,
      });
    } catch (e) {
      console.error('Error al sincronizar KPIs de ventas:', e);
      reportHandledError(e, { feature: 'dashboard:kpis-ventas' });
      setKpiVentas({ total: 0, count: 0, ticketPromedio: 0 });
    } finally {
      setLoadingKpis(false);
    }
  };

  useEffect(() => {
    if (!orgId) return;
    fetchSalesKpis();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orgId, activeSiteId, kpiDateRange.from, kpiDateRange.to]);

  // ✅ Eliminado: loadSitios. SiteContext es la fuente única de verdad
  // para la lista de sucursales.

  const fetchTopProducts = async () => {
    setLoadingTopProds(true);
    try {
      const fromISO = `${topProdFilter.from}T00:00:00`;
      const toISO = `${topProdFilter.to}T23:59:59`;

      let itemsQuery = supabase.from('sale_items')
        .select('quantity, product_id, products!vinc_prod(name), sales!inner(created_at, organization_id, site_id)')
        .eq('sales.organization_id', orgId)
        .gte('sales.created_at', fromISO)
        .lte('sales.created_at', toISO);

      if (activeSiteId) itemsQuery = itemsQuery.eq('sales.site_id', activeSiteId);
      const { data: saleItems } = await itemsQuery;

      const map = new Map();
      (saleItems || []).forEach((it) => {
        const key = it.product_id || 'unknown';
        const name = Array.isArray(it.products) ? it.products[0]?.name : it.products?.name || 'Producto';
        map.set(key, { name, qty: (map.get(key)?.qty || 0) + Number(it.quantity) });
      });

      // TOP 5
      const topList = Array.from(map.values()).sort((a, b) => b.qty - a.qty).slice(0, 5);
      setTopProductsList(topList);

    } catch (e) {
      console.error(e);
    } finally {
      setLoadingTopProds(false);
    }
  };

  const fetchDashboardData = async () => {
    setLoading(true);
    try {
      const siteParam = activeSiteId || null;

      // LLamada ultra-rápida a la Base de Datos
      const { data, error } = await supabase.rpc('get_dashboard_metrics', {
        p_organization_id: orgId,
        p_site_id: siteParam
      });

      if (error) throw error;

      const ventasMes = Number(data.ventasMes || 0);
      const gastosMes = Number(data.gastosMes || 0);
      const costoVentasMes = Number(data.costoVentasMes || 0);

      const porcentajeMargen = ventasMes > 0 ? (ventasMes - costoVentasMes) / ventasMes : 0;
      let puntoEquilibrio = porcentajeMargen > 0 ? gastosMes / porcentajeMargen : 0;

      setMetrics(prev => ({
        ...prev,
        ventasHoy: Number(data.ventasHoy || 0),
        ventasMes: ventasMes,
        gastosMes: gastosMes,
        stockBajo: Number(data.stockBajo || 0),
        bajaRentabilidad: Number(data.bajaRentabilidad || 0),
        pedidosWeb: Number(data.pedidosWeb || 0),
        proyIngresosHoy: Number(data.proyIngresosHoy || 0),
        proyEgresosHoy: Number(data.proyEgresosHoy || 0),
        bancosNegativos: Number(data.bancosNegativos || 0),
        saldoCaja: Number(data.saldoCaja || 0),
        puntoEquilibrio,
        porcentajeMargen,
        // El RPC get_dashboard_metrics (optimizado) devuelve ventasDiarias como
        // array de objetos { fecha, monto }. El gráfico de barras espera números,
        // así que normalizamos a monto. Toleramos también la forma vieja (números
        // sueltos) por si quedó algún entorno con la versión previa del RPC.
        monthlyData: Array.isArray(data.ventasDiarias)
          ? data.ventasDiarias.map((d) =>
              typeof d === 'object' && d !== null ? Number(d.monto || 0) : Number(d || 0)
            )
          : []
      }));

    } catch (e) {
      console.error('Error al cargar Dashboard Data:', e.message || e);
    } finally {
      setLoading(false);
    }
  };

  const todayYMD = useMemo(() => new Date().toLocaleDateString('en-CA'), []);

  const formatAlertDateTime = (value) => {
    if (!value) return 'Sin fecha';
    const date = new Date(value);
    return date.toLocaleString('es-AR', {
      day: '2-digit',
      month: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const fetchAlertDetails = async () => {
    if (!orgId) return;

    setLoadingAlerts(true);
    try {
      const ordersQuery = supabase
        .from('store_orders')
        .select('id, tracking_code, status, created_at, customer_snapshot, customer_phone, store_slug, sale:sales!store_orders_sale_id_fkey(total, site_id)')
        .eq('organization_id', orgId)
        .eq('status', 'recibido')
        .order('created_at', { ascending: false })
        .limit(5);

      let dueTodayQuery = supabase
        .from('proyecciones')
        .select('id, concepto, monto, fecha, tipo, categoria, sitio_id')
        .eq('organization_id', orgId)
        .eq('fecha', todayYMD)
        .order('tipo', { ascending: true })
        .limit(6);

      if (activeSiteId) {
        dueTodayQuery = dueTodayQuery.or(`sitio_id.eq.${activeSiteId},sitio_id.is.null`);
      }

      const [ordersRes, stockRes, marginRes, dueTodayRes, storeSiteContextRes] = await Promise.all([
        ordersQuery,
        fetchAllPaginated(() =>
          supabase
            .from('products')
            .select('id, name, min_stock, active, stock_levels(quantity, site_id), product_variants(stock_quantity), product_categories(name)')
            .eq('organization_id', orgId)
            .eq('active', true)
            .order('name', { ascending: true })
        ),
        fetchAllPaginated(() =>
          supabase
            .from('products')
            .select('id, name, sell_price, cost_price, target_margin, active, product_categories(name)')
            .eq('organization_id', orgId)
            .eq('active', true)
            .order('name', { ascending: true })
        ),
        dueTodayQuery,
        fetchStoreSiteContext({ supabase, organizationId: orgId }),
      ]);

      const orders = (ordersRes.data || [])
        .filter(isStoreOrderFromWeb)
        .filter((order) => {
          if (!activeSiteId) return true;
          return resolveStoreOrderSiteId(order, storeSiteContextRes.data) === activeSiteId;
        })
        .map((order) => ({
        id: order.id,
        label: `#${order.tracking_code || order.id?.slice?.(0, 8) || 'PEDIDO'}`,
        meta: `${order.customer_snapshot?.name || order.customer_phone || 'Cliente web'}${order.sale?.total ? ` • ${formatMoney(order.sale.total)}` : ''} • ${formatAlertDateTime(order.created_at)}`,
        onClick: () => navigate(`/sistema/pedido-web-detalle/${order.id}`),
      }));

      const lowStock = (stockRes || [])
        .map((product) => {
          const quantity = getProductStockForSite(product, activeSiteId || null);
          const minStock = Number(product.min_stock || 0);
          const isLow = minStock > 0 ? quantity <= minStock : quantity <= 0;
          if (!isLow) return null;
          return {
            id: product.id,
            label: product.name,
            meta: `Stock ${quantity} • Mínimo ${minStock}${product.product_categories?.name ? ` • ${product.product_categories.name}` : ''}`,
            quantity,
            minStock,
            onClick: () => navigate(`/sistema/producto-detalle/${product.id}`),
          };
        })
        .filter(Boolean)
        .sort((a, b) => (a.quantity - a.minStock) - (b.quantity - b.minStock))
        .slice(0, 5);

      const lowMargin = (marginRes || [])
        .map((product) => {
          const sellPrice = Number(product.sell_price || 0);
          const costPrice = Number(product.cost_price || 0);
          const targetMargin = Number(product.target_margin || 0);
          const realMargin = costPrice > 0 ? (((sellPrice - costPrice) / costPrice) * 100) : null;
          const needsAttention = costPrice <= 0 || realMargin == null || realMargin < targetMargin;
          if (!needsAttention) return null;
          return {
            id: product.id,
            label: product.name,
            meta:
              costPrice <= 0
                ? 'Sin costo cargado'
                : `Margen ${Number(realMargin || 0).toFixed(1)}% • Objetivo ${Number(targetMargin || 0).toFixed(1)}%`,
            gap: costPrice <= 0 ? -9999 : Number(realMargin || 0) - Number(targetMargin || 0),
            onClick: () => navigate(`/sistema/producto-detalle/${product.id}`),
          };
        })
        .filter(Boolean)
        .sort((a, b) => a.gap - b.gap)
        .slice(0, 5);

      // ✅ FIX bug #12 multi-sucursal: marcamos visiblemente los vencimientos
      // "Consolidado" (sin sitio_id) cuando el usuario está parado en una sucursal
      // específica. Antes esos compromisos aparecían en el dashboard de cada
      // sucursal sin marca, así que parecía que cada local los debía pagar.
      const dueToday = (dueTodayRes.data || []).map((movement) => {
        const isConsolidated = !movement.sitio_id;
        const baseMeta = `${movement.tipo === 'ingreso' ? 'Cobro' : 'Pago'} • ${formatMoney(movement.monto)}${movement.categoria ? ` • ${movement.categoria}` : ''}`;
        const scopeTag = activeSiteId && isConsolidated ? ' • Consolidado' : '';
        return {
          id: movement.id,
          label: movement.concepto || (movement.tipo === 'ingreso' ? 'Cobro proyectado' : 'Pago proyectado'),
          meta: `${baseMeta}${scopeTag}`,
          onClick: () =>
            navigate('/sistema/flujo-fondos', {
              state: {
                dashboardFocus: {
                  type: 'vencimientos',
                  date: movement.fecha,
                  movementId: movement.id,
                },
              },
            }),
        };
      });

      setAlertDetails({ orders, lowStock, lowMargin, dueToday });
    } catch (error) {
      console.error('Error al cargar detalle de alertas:', error);
    } finally {
      setLoadingAlerts(false);
    }
  };

  const maxVal = Math.max(...metrics.monthlyData) || 1000;
  const goToVentas = () => navigate('/sistema/ventas-historial');
  const goToStock = () => navigate('/sistema/inventario', { state: { dashboardFocus: { type: 'stock' } } });
  const goToBancos = () => navigate('/sistema/bancos');
  const goToRentabilidad = () => navigate('/sistema/rentabilidad-ventas');
  const goToFlujo = () => navigate('/sistema/flujo-fondos', { state: { dashboardFocus: { type: 'vencimientos', date: todayYMD } } });
  const goToWeb = () => navigate('/sistema/pedidos-web', { state: { dashboardFocus: { type: 'pedidos' } } });
  const goToProductos = () => navigate('/sistema/productos');

  const equilibriumTone = metrics.ventasMes >= metrics.puntoEquilibrio ? "emerald" : "amber";

  const attentionAlerts = useMemo(() => ([
    {
      key: 'pedidosWeb',
      count: metrics.pedidosWeb,
      title: 'Pedidos Web',
      summary: 'nuevos para armar.',
      description: 'Pedidos recién ingresados que todavía requieren preparación o toma por parte del equipo.',
      icon: <ShoppingCart />,
      tone: 'emerald',
      items: alertDetails.orders,
      onViewAll: goToWeb,
    },
    {
      key: 'stockBajo',
      count: metrics.stockBajo,
      title: 'Stock Crítico',
      summary: 'por debajo del mínimo.',
      description: 'Productos con stock comprometido o por debajo del umbral operativo para el sitio actual o el consolidado.',
      icon: <Package />,
      tone: 'amber',
      items: alertDetails.lowStock,
      onViewAll: goToStock,
    },
    {
      key: 'bajaRentabilidad',
      count: metrics.bajaRentabilidad,
      title: 'Baja Rentabilidad',
      summary: 'con margen por debajo del ideal.',
      description: 'Productos con margen real por debajo del objetivo configurado o sin costo maestro cargado.',
      icon: <TrendingDown />,
      tone: 'rose',
      items: alertDetails.lowMargin,
      onViewAll: goToRentabilidad,
    },
    {
      key: 'vencimientosHoy',
      count: metrics.proyEgresosHoy + metrics.proyIngresosHoy,
      title: 'Vencimientos Hoy',
      summary: 'cobros y pagos vencen hoy.',
      description: 'Movimientos proyectados con fecha de hoy que conviene revisar o confirmar para no perder trazabilidad.',
      icon: <Clock />,
      tone: 'blue',
      items: alertDetails.dueToday,
      onViewAll: goToFlujo,
    },
  ]), [
    alertDetails.dueToday,
    alertDetails.lowMargin,
    alertDetails.lowStock,
    alertDetails.orders,
    goToFlujo,
    goToRentabilidad,
    goToStock,
    goToWeb,
    metrics.bajaRentabilidad,
    metrics.pedidosWeb,
    metrics.proyEgresosHoy,
    metrics.proyIngresosHoy,
    metrics.stockBajo,
  ]);

  const activeAttentionAlerts = useMemo(
    () => attentionAlerts.filter((alert) => Number(alert.count || 0) > 0),
    [attentionAlerts],
  );

  useEffect(() => {
    if (!activeAttentionAlerts.length) {
      setExpandedAlertKey(null);
      return;
    }
    setExpandedAlertKey((prev) => {
      if (prev && activeAttentionAlerts.some((alert) => alert.key === prev)) return prev;
      return activeAttentionAlerts[0].key;
    });
  }, [activeAttentionAlerts]);

  const goToPrimaryAlertPage = () => {
    const firstActiveAlert = activeAttentionAlerts[0];
    if (!firstActiveAlert) return;
    firstActiveAlert.onViewAll?.();
  };

  // El Dashboard ahora trabaja exclusivamente por sucursal: el consolidado
  // se ve en Reportes Inteligentes. Si el usuario no tiene una sucursal
  // activa (recién creó la cuenta, o limpió la preferencia), mostramos un
  // mensaje guía en vez de cargar métricas vacías.
  if (!activeSiteId) {
    return (
      <div className="imp-page imp-container animate-nova-in">
        <div className="imp-header mb-3">
          <div className="flex items-center gap-3 min-w-0">
            <div className="w-10 h-10 bg-[#0B2A36] rounded-2xl flex items-center justify-center shadow-md shrink-0">
              <Activity size={18} className="text-[#3FF2AA]" />
            </div>
            <div className="min-w-0">
              <h1 className="text-xl md:text-2xl font-black text-slate-800 tracking-tight uppercase truncate">Panel de gestión</h1>
              <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400">
                Control de gestión por sucursal
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white border border-slate-200 rounded-2xl shadow-sm p-8 max-w-2xl mx-auto text-center">
          <div className="w-14 h-14 mx-auto bg-[#51A7FD]/10 rounded-2xl flex items-center justify-center mb-4">
            <MapPin size={26} className="text-[#51A7FD]" />
          </div>
          <h2 className="text-base font-black text-slate-800 uppercase tracking-tight mb-2">
            Elegí una sucursal
          </h2>
          <p className="text-[11px] font-bold text-slate-500 leading-relaxed mb-5">
            El panel ahora trabaja siempre sobre una sucursal específica. Seleccioná la
            sucursal desde el selector del topbar (arriba a la izquierda) para ver sus
            KPIs y alertas operativas.
          </p>
          <div className="rounded-xl border border-dashed border-slate-200 bg-slate-50 px-4 py-3">
            <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400 mb-2">
              ¿Buscás el consolidado?
            </p>
            <button
              type="button"
              onClick={() => navigate('/sistema/reportes-inteligentes')}
              className="px-4 py-2 rounded-xl bg-[#0B2A36] text-white hover:opacity-90 transition-all text-[10px] font-black uppercase tracking-widest inline-flex items-center gap-2"
            >
              <BarChart3 size={14} /> Ir a Reportes Inteligentes
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div data-onboarding-id="dashboard-content-area" className="imp-page imp-container animate-nova-in">

      {/* HEADER (gap reducido) */}
      <div className="imp-header mb-3">
        <div className="flex items-center gap-3 min-w-0">
          <div className="w-10 h-10 bg-[#0B2A36] rounded-2xl flex items-center justify-center shadow-md shrink-0">
            <Activity size={18} className="text-[#3FF2AA]" />
          </div>
          <div className="min-w-0">
            <h1 className="text-xl md:text-2xl font-black text-slate-800 tracking-tight uppercase truncate">Panel de gestión</h1>
            <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400">
              Control de gestión — <span className="text-[#51A7FD]">{siteLabel}</span>
            </p>
          </div>
        </div>

        <div className="imp-header-actions">
          {/* Filtro de Fecha para KPIs de Ventas */}
          <div className="relative" ref={kpiDatePickerRef}>
            <button
              onClick={() => setShowKpiDatePicker((v) => !v)}
              className="flex items-center gap-2 bg-white border border-slate-200 rounded-2xl px-3 py-2 shadow-sm transition-all hover:border-[#51A7FD]"
              title="Filtrar KPIs de ventas por rango de fecha"
            >
              <Calendar size={14} className="text-[#51A7FD]" />
              <span className="text-[10px] font-black uppercase tracking-widest text-slate-600">
                {kpiRangeLabel}
              </span>
              <ChevronRight
                size={12}
                className={`text-slate-300 transition-transform ${showKpiDatePicker ? 'rotate-90' : ''}`}
              />
            </button>
            {showKpiDatePicker && (
              <div className="absolute right-0 top-full mt-2 w-72 bg-white rounded-2xl border border-slate-200 shadow-xl z-50 p-4 animate-in fade-in slide-in-from-top-2">
                <p className="text-[9px] font-black uppercase tracking-widest text-slate-400 mb-2">
                  Atajos
                </p>
                <div className="grid grid-cols-2 gap-2 mb-3">
                  <button
                    onClick={() => applyKpiPreset('hoy')}
                    className="px-2 py-1.5 rounded-xl bg-slate-50 hover:bg-[#3FF2AA]/20 text-[9px] font-black uppercase tracking-widest text-slate-600 transition-all"
                  >
                    Hoy
                  </button>
                  <button
                    onClick={() => applyKpiPreset('ayer')}
                    className="px-2 py-1.5 rounded-xl bg-slate-50 hover:bg-[#3FF2AA]/20 text-[9px] font-black uppercase tracking-widest text-slate-600 transition-all"
                  >
                    Ayer
                  </button>
                  <button
                    onClick={() => applyKpiPreset('semana')}
                    className="px-2 py-1.5 rounded-xl bg-slate-50 hover:bg-[#3FF2AA]/20 text-[9px] font-black uppercase tracking-widest text-slate-600 transition-all"
                  >
                    7 días
                  </button>
                  <button
                    onClick={() => applyKpiPreset('mes')}
                    className="px-2 py-1.5 rounded-xl bg-slate-50 hover:bg-[#3FF2AA]/20 text-[9px] font-black uppercase tracking-widest text-slate-600 transition-all"
                  >
                    Mes actual
                  </button>
                </div>
                <p className="text-[9px] font-black uppercase tracking-widest text-slate-400 mb-2">
                  Rango personalizado
                </p>
                <div className="space-y-2">
                  <div>
                    <label className="text-[8px] font-bold uppercase tracking-widest text-slate-400 ml-1">
                      Desde
                    </label>
                    <input
                      type="date"
                      value={kpiDateRange.from}
                      max={kpiDateRange.to}
                      onChange={(e) => setKpiDateRange((prev) => ({ ...prev, from: e.target.value }))}
                      className="w-full mt-1 px-3 py-2 bg-slate-50 border border-slate-200 rounded-xl text-[10px] font-bold text-slate-700 outline-none focus:border-[#51A7FD]"
                    />
                  </div>
                  <div>
                    <label className="text-[8px] font-bold uppercase tracking-widest text-slate-400 ml-1">
                      Hasta
                    </label>
                    <input
                      type="date"
                      value={kpiDateRange.to}
                      min={kpiDateRange.from}
                      onChange={(e) => setKpiDateRange((prev) => ({ ...prev, to: e.target.value }))}
                      className="w-full mt-1 px-3 py-2 bg-slate-50 border border-slate-200 rounded-xl text-[10px] font-bold text-slate-700 outline-none focus:border-[#51A7FD]"
                    />
                  </div>
                </div>
                <p className="mt-3 text-[8px] font-bold text-slate-400 leading-snug">
                  Estos KPIs usan la misma lógica que el Historial de Ventas.
                </p>
              </div>
            )}
          </div>

          {deferredPrompt && (
            <button onClick={handleInstallClick} className="px-5 py-2.5 rounded-2xl bg-[#51A7FD] text-white shadow-lg hover:shadow-xl transition-all text-[10px] font-black uppercase tracking-widest flex items-center gap-2">
              <Download size={16} /> Instalar App
            </button>
          )}

          <button
            onClick={() => { fetchDashboardData(); fetchSalesKpis(); }}
            data-onboarding-id="dashboard-refresh"
            className="px-5 py-2.5 rounded-2xl bg-[#3FF2AA] text-[#0B2A36] shadow-lg hover:shadow-xl transition-all text-[10px] font-black uppercase tracking-widest flex items-center gap-2"
          >
            <RefreshCcw size={16} className={(loading || loadingKpis) ? 'animate-spin' : ''} /> Actualizar
          </button>
        </div>
      </div>

      {/* BIENVENIDA POR ROL (onboarding F6 — usuarios invitados no-admin) */}
      {showRoleWelcome && (
        <div className="mb-3 rounded-2xl border border-sky-100 bg-gradient-to-br from-sky-50 to-white p-5 shadow-sm">
          <div className="flex items-start justify-between gap-3">
            <div className="flex items-start gap-3">
              <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-sky-600 text-white">
                <Info size={20} />
              </span>
              <div>
                <h3 className="text-base font-semibold text-slate-800">¡Bienvenido al equipo!</h3>
                <p className="mt-0.5 text-sm text-slate-500">
                  Tu trabajo diario arranca en el Punto de Venta: desde ahi cobras y registras las ventas del local.
                  Al entrar a cada modulo te vamos a mostrar una guia rapida.
                </p>
                <div className="mt-3 flex flex-wrap gap-2">
                  <button
                    type="button"
                    onClick={() => dismissRoleWelcome(true)}
                    className="inline-flex items-center gap-1 rounded-lg bg-sky-600 px-3 py-1.5 text-xs font-semibold text-white transition hover:bg-sky-700"
                  >
                    Ir al punto de venta <ChevronRight size={14} />
                  </button>
                  <button
                    type="button"
                    onClick={() => dismissRoleWelcome(false)}
                    className="inline-flex items-center gap-1 rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-xs font-semibold text-slate-600 transition hover:bg-slate-50"
                  >
                    Explorar por mi cuenta
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* El checklist "Empezá a operar" ya no vive en el dashboard: se entra
          desde el botón del topbar, que lleva a /sistema/progreso. Acá se
          mantiene solo la telemetría del funnel (checklist_completed). */}

      {/* KPIS (Intactos en diseño, gap inferior reducido) */}
      <div className="imp-kpis mb-2">
        <DashboardKPI
          title={isKpiRangeToday ? 'Ventas Hoy' : 'Ventas del Período'}
          value={formatMoney(kpiVentasDisplay.total)}
          subtext={
            kpiVentasDisplay.count > 0
                ? `${kpiVentasDisplay.count} venta${kpiVentasDisplay.count !== 1 ? 's' : ''} · Ticket prom. ${formatMoney(kpiVentasDisplay.ticketPromedio)}`
                : `Total facturado · ${kpiRangeLabel}`
          }
          icon={<DollarSign />}
          tone="emerald"
          onClick={goToVentas}
          dataOnboardingId="dashboard-kpi-ventas"
        />
        <DashboardKPI
          title="Saldo Financiero"
          value={formatMoney(metrics.saldoCaja)}
          subtext="Liquidez en bancos"
          icon={<Wallet />}
          tone={metrics.bancosNegativos > 0 ? "rose" : "blue"}
          onClick={goToBancos}
        />
        <DashboardKPI
          title="Meta de Equilibrio"
          value={formatMoney(metrics.puntoEquilibrio)}
          subtext="Click para rentabilidad"
          icon={<Target />}
          tone={equilibriumTone}
          onClick={goToRentabilidad}
        />
        <DashboardKPI
          title="Alertas Activas"
          value={metrics.stockBajo + metrics.pedidosWeb + metrics.bajaRentabilidad + metrics.proyEgresosHoy + metrics.proyIngresosHoy}
          subtext="Requieren atención"
          icon={<AlertCircle />}
          onClick={goToPrimaryAlertPage}
          tone={(metrics.stockBajo > 0 || metrics.pedidosWeb > 0 || metrics.bajaRentabilidad > 0 || metrics.proyEgresosHoy > 0 || metrics.proyIngresosHoy > 0) ? "amber" : "slate"}
        />
      </div>

      {/* CONTENIDO PRINCIPAL (Gaps reducidos de gap-3 a gap-2) */}
      <div className="grid grid-cols-1 xl:grid-cols-3 gap-2">

        {/* GRÁFICO TENDENCIA */}
        <div
          data-onboarding-id="dashboard-sales-trend"
          className="xl:col-span-2 bg-white p-6 rounded-2xl border border-slate-200 shadow-sm h-[500px] flex flex-col"
        >
          <div className="flex items-center justify-between mb-10">
            <h3 className="font-black text-slate-800 uppercase text-[11px] tracking-widest flex items-center gap-2">
              <TrendingUp size={18} className="text-[#51A7FD]" /> Tendencia de Ventas (Día a Día)
            </h3>

            {/* Texto Dinámico de Cabecera */}
            <div className="text-right transition-all">
              <p className="text-[8px] font-black text-slate-400 uppercase tracking-widest transition-colors">
                {hoveredDay !== null ? `Ventas del Día ${hoveredDay + 1}` : 'Acumulado Mes'}
              </p>
              <p className="text-lg font-black text-[#51A7FD] transition-colors">
                {hoveredDay !== null ? formatMoney(metrics.monthlyData[hoveredDay]) : formatMoney(metrics.ventasMes)}
              </p>
            </div>
          </div>

          <div className="flex-1 flex items-end gap-1.5 pb-8 relative">
            <div className="absolute inset-0 flex flex-col justify-between pointer-events-none opacity-5 z-0 pb-8">
              {[...Array(5)].map((_, i) => <div key={i} className="border-t border-slate-900 w-full h-0 border-dashed"></div>)}
            </div>

            {metrics.monthlyData.map((val, i) => {
              const heightPct = (val / (maxVal || 1)) * 100;
              return (
                <div
                  key={i}
                  className="flex-1 h-full flex flex-col justify-end items-center relative group"
                  onMouseEnter={() => setHoveredDay(i)}
                  onMouseLeave={() => setHoveredDay(null)}
                >
                  {/* BARRA */}
                  <div
                    onClick={goToVentas}
                    className="w-full bg-[#51A7FD]/20 border-t-2 border-[#51A7FD] rounded-t-sm transition-all duration-300 group-hover:bg-[#3ff2aa] group-hover:border-[#3ff2aa] cursor-pointer relative"
                    style={{ height: `${heightPct}%`, minHeight: val > 0 ? '4px' : '0px' }}
                  >
                    {/* TOOLTIP HORIZONTAL FIJADO AL TOPE DE LA BARRA */}
                    {val > 0 && (
                      <div className="absolute -top-10 left-1/2 -translate-x-1/2 opacity-0 group-hover:opacity-100 transition-opacity z-[100] pointer-events-none flex flex-col items-center">
                        <span className="bg-[#0B2A36] text-white text-[10px] font-bold px-2 py-1 rounded-md shadow-lg whitespace-nowrap">
                          {formatMoney(val)}
                        </span>
                        {/* Triángulo del globo */}
                        <div className="w-0 h-0 border-l-[4px] border-l-transparent border-r-[4px] border-r-transparent border-t-[4px] border-t-[#0B2A36]"></div>
                      </div>
                    )}
                  </div>
                  {/* TEXTO INFERIOR (Día) */}
                  <div className="absolute -bottom-6 w-full text-center">
                    <span className="text-[8px] font-bold text-slate-400">{i + 1}</span>
                  </div>
                </div>
              )
            })}
          </div>
          <div className="mt-4 text-center">
            <p className="text-[9px] font-black uppercase tracking-[0.2em] text-slate-300">Días del mes actual</p>
          </div>
        </div>

        {/* COLUMNA LATERAL (Gaps reducidos de space-y-3 a space-y-2) */}
        <div className="space-y-2">

          {/* TOP PRODUCTOS (Top 5 + Filtro) */}
          <div className="bg-white p-5 rounded-2xl border border-slate-200 shadow-sm flex flex-col">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-black text-slate-800 uppercase text-[11px] tracking-widest flex items-center gap-2">
                <Package size={18} className="text-[#51A7FD]" /> Más Vendidos
              </h3>
              <button
                onClick={() => setShowTopProdFilter(!showTopProdFilter)}
                className={`p-1.5 rounded-lg transition-colors ${showTopProdFilter ? 'bg-blue-50 text-[#51A7FD]' : 'bg-slate-50 text-slate-400 hover:text-slate-600'}`}
              >
                <Filter size={14} />
              </button>
            </div>

            {/* Panel de Filtro */}
            {showTopProdFilter && (
              <div className="flex gap-2 mb-4 p-2 bg-slate-50 border border-slate-100 rounded-xl animate-in zoom-in-95">
                <input
                  type="date"
                  value={topProdFilter.from}
                  onChange={(e) => setTopProdFilter({ ...topProdFilter, from: e.target.value })}
                  className="w-full bg-white px-2 py-1.5 rounded-lg text-[9px] font-bold text-slate-600 outline-none border border-slate-200 shadow-sm uppercase"
                />
                <input
                  type="date"
                  value={topProdFilter.to}
                  onChange={(e) => setTopProdFilter({ ...topProdFilter, to: e.target.value })}
                  className="w-full bg-white px-2 py-1.5 rounded-lg text-[9px] font-bold text-slate-600 outline-none border border-slate-200 shadow-sm uppercase"
                />
              </div>
            )}

            <div className="flex-1 overflow-y-auto custom-scrollbar pr-1 space-y-2 min-h-[160px]">
              {loadingTopProds ? (
                <div className="flex items-center justify-center text-slate-300 h-full">
                  <RefreshCcw className="animate-spin" size={20} />
                </div>
              ) : topProductsList.length === 0 ? (
                <div className="flex flex-col items-center justify-center opacity-40 h-full">
                  <span className="text-[9px] font-black uppercase tracking-widest">Sin ventas</span>
                </div>
              ) : (
                topProductsList.map((p, i) => (
                  <div key={i} className="flex items-center justify-between p-2.5 hover:bg-slate-50 rounded-xl transition-all border border-transparent hover:border-slate-100">
                    <div className="flex items-center gap-3 overflow-hidden">
                      <div className="w-6 h-6 bg-slate-100 text-slate-600 rounded-lg flex items-center justify-center text-[9px] font-black shrink-0">{i + 1}</div>
                      <p className="text-[10px] font-black text-slate-700 uppercase truncate">{p.name}</p>
                    </div>
                    <span className="text-[9px] font-black text-slate-400 bg-slate-50 px-2.5 py-1 rounded-lg shrink-0">{p.qty} UN</span>
                  </div>
                ))
              )}
            </div>
          </div>

          {/* ALERTAS MEJORADAS (Acción Requerida) */}
          <div data-onboarding-id="dashboard-alertas" className="bg-white p-5 rounded-2xl border border-slate-200 shadow-sm">
            <h3 className="font-black text-slate-800 uppercase text-[11px] tracking-widest mb-4 flex items-center gap-2">
              <BadgeAlert size={18} className="text-amber-500" /> Atención Requerida
            </h3>

            <div className="space-y-3">
              {activeAttentionAlerts.map((alert) => (
                <AttentionAlertCard
                  key={alert.key}
                  title={alert.title}
                  summary={alert.summary}
                  description={alert.description}
                  count={alert.count}
                  icon={alert.icon}
                  tone={alert.tone}
                  expanded={expandedAlertKey === alert.key}
                  loading={loadingAlerts}
                  items={alert.items}
                  onToggle={() => setExpandedAlertKey((prev) => (prev === alert.key ? null : alert.key))}
                  onViewAll={alert.onViewAll}
                />
              ))}

              {activeAttentionAlerts.length === 0 && (
                <div className="py-6 text-center border-2 border-dashed border-slate-50 rounded-2xl">
                  <CheckCircle2 size={16} className="mx-auto text-emerald-400 mb-2 opacity-50" />
                  <p className="text-[10px] font-black text-slate-300 uppercase tracking-[0.25em]">Todo en orden</p>
                </div>
              )}

            </div>
          </div>

        </div>
      </div>

      <style>{`
        .animate-nova-in { animation: novaFadeIn 0.3s cubic-bezier(0.16, 1, 0.3, 1) forwards; }
        @keyframes novaFadeIn { from { opacity: 0; transform: translateY(8px); } to { opacity: 1; transform: translateY(0); } }
        .custom-scrollbar::-webkit-scrollbar { width: 4px; }
        .custom-scrollbar::-webkit-scrollbar-thumb { background: #e2e8f0; border-radius: 20px; }
      `}</style>
    </div>
  );
};

export default Dashboard;
